import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import Navbar from "@/components/landing/Navbar";
import Hero from "@/components/landing/Hero";
import Marquee from "@/components/landing/Marquee";
import Dimora from "@/components/landing/Dimora";
import Spazi from "@/components/landing/Spazi";
import Gallery from "@/components/landing/Gallery";
import GiardinoSpa from "@/components/landing/GiardinoSpa";
import Servizi from "@/components/landing/Servizi";
import Disponibilita from "@/components/landing/Disponibilita";
import Prenota from "@/components/landing/Prenota";
import Host from "@/components/landing/Host";
import Dintorni from "@/components/landing/Dintorni";
import Footer from "@/components/landing/Footer";
import { toKey } from "@/lib/dates";

const PRIORITY = { direct: 0, booking: 1, airbnb: 2, bnb: 3 };

export default function LandingPage() {
  const [busyRanges, setBusyRanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [range, setRange] = useState({ from: null, to: null });

  useEffect(() => {
    api
      .get("/availability")
      .then((res) => {
        setBusyRanges(res.data.busy || []);
        setUpdatedAt(res.data.updated_at);
      })
      .catch(() => toast.error("Impossibile caricare la disponibilità. Riprova tra poco."))
      .finally(() => setLoading(false));
  }, []);

  const busyMap = useMemo(() => {
    const map = new Map();
    for (const r of busyRanges) {
      const start = new Date(r.start + "T00:00:00");
      const end = new Date(r.end + "T00:00:00");
      for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
        const key = toKey(d);
        const existing = map.get(key);
        if (!existing || PRIORITY[r.source] < PRIORITY[existing]) map.set(key, r.source);
      }
    }
    return map;
  }, [busyRanges]);

  const rangeHasBusy = useCallback(
    (from, to) => {
      for (let d = new Date(from); d < to; d.setDate(d.getDate() + 1)) {
        if (busyMap.has(toKey(d))) return true;
      }
      return false;
    },
    [busyMap]
  );

  const handleSelect = useCallback(
    (day) => {
      setRange((prev) => {
        if (!prev.from || (prev.from && prev.to)) return { from: day, to: null };
        if (day <= prev.from) return { from: day, to: null };
        if (rangeHasBusy(prev.from, day)) {
          toast.error("L'intervallo selezionato include giorni già prenotati.");
          return { from: day, to: null };
        }
        return { from: prev.from, to: day };
      });
    },
    [rangeHasBusy]
  );

  const handleSetDate = useCallback(
    (day, isFrom) => {
      setRange((prev) => {
        if (isFrom) {
          const next = { from: day, to: prev.to && day && prev.to > day ? prev.to : null };
          if (next.from && next.to && rangeHasBusy(next.from, next.to)) {
            toast.error("L'intervallo include giorni già prenotati.");
            return { from: next.from, to: null };
          }
          return next;
        }
        if (day && prev.from && day > prev.from) {
          if (rangeHasBusy(prev.from, day)) {
            toast.error("L'intervallo include giorni già prenotati.");
            return prev;
          }
          return { from: prev.from, to: day };
        }
        return { ...prev, to: day };
      });
    },
    [rangeHasBusy]
  );

  return (
    <div data-testid="landing-page" className="bg-linen">
      <div className="grain-overlay" />
      <Navbar />
      <Hero />
      <Marquee />
      <Dimora />
      <Spazi />
      <Gallery />
      <GiardinoSpa />
      <Servizi />
      <Disponibilita busyMap={busyMap} loading={loading} updatedAt={updatedAt} range={range} onSelect={handleSelect} />
      <Prenota range={range} onSelect={handleSetDate} busyMap={busyMap} />
      <Host />
      <Dintorni />
      <Footer />
    </div>
  );
}
